/**
 * The single way the app talks to the backend.
 *
 * Before this, `localStorage.getItem("vh_token")` appeared in 22 components, each
 * building its own headers and none checking for 401. When a token expired the app
 * kept rendering stale data with the user apparently still logged in, instead of
 * sending them to the login page.
 *
 * Everything goes through `apiFetch`, which:
 *   - attaches the bearer token,
 *   - turns a 401 into a single, app-wide logout + redirect,
 *   - normalises errors into ApiError so callers can branch on `code`/`status`
 *     instead of parsing response bodies themselves.
 */

export const TOKEN_KEY = "vh_token";

/** Fired when the server rejects our token. AuthProvider listens and logs out. */
export const UNAUTHORIZED_EVENT = "api:unauthorized";

export function getToken(): string | null {
  if (typeof window === "undefined") return null;
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string) {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearToken() {
  // Guarded like getToken: a 401 during SSR (no token to send in the first place)
  // would otherwise touch localStorage on the server and crash the render.
  if (typeof window === "undefined") return;
  localStorage.removeItem(TOKEN_KEY);
}

export class ApiError extends Error {
  readonly status: number;
  /** Machine-readable detail when the backend sends one (e.g. "EMAIL_NOT_VERIFIED"). */
  readonly code?: string;
  /** Parsed response body, when there was one. */
  readonly body?: unknown;

  constructor(status: number, message: string, code?: string, body?: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.body = body;
  }

  /** Rate limited — worth showing a "slow down" message rather than a generic error. */
  get isRateLimited() {
    return this.status === 429;
  }

  /** Request never reached the server. */
  get isNetwork() {
    return this.status === 0;
  }
}

type RequestOptions = Omit<RequestInit, "body"> & {
  /** Serialised as JSON with the right content-type. */
  json?: unknown;
  /** Send without the Authorization header (public endpoints). */
  anonymous?: boolean;
  /**
   * Suppress the global logout on 401. Used by the login call itself, where a 401
   * means "wrong password", not "session expired".
   */
  skipAuthRedirect?: boolean;
};

function extractDetail(body: unknown): { message?: string; code?: string } {
  if (!body || typeof body !== "object") return {};
  const detail = (body as { detail?: unknown }).detail;
  if (typeof detail === "string") {
    // FastAPI sends both human sentences and SCREAMING_CODES through `detail`.
    const looksLikeCode = /^[A-Z][A-Z0-9_]{2,}$/.test(detail);
    return looksLikeCode ? { code: detail, message: detail } : { message: detail };
  }
  if (detail && typeof detail === "object" && !Array.isArray(detail)) {
    // Purchase and pricing errors carry a structured reason: the code drives the
    // translated message, and the rest of the object (price, provider_cost for admins)
    // stays available on ApiError.body.
    const { code, message } = detail as { code?: unknown; message?: unknown };
    if (typeof code === "string") {
      return { code, message: typeof message === "string" ? message : code };
    }
  }
  if (Array.isArray(detail) && detail.length) {
    // Pydantic validation errors.
    const first = detail[0] as { msg?: string };
    return { message: first?.msg ?? "Validation error", code: "VALIDATION_ERROR" };
  }
  return {};
}

export async function apiFetch<T = unknown>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const { json, anonymous, skipAuthRedirect, headers, ...rest } = options;

  const finalHeaders = new Headers(headers);
  if (json !== undefined) finalHeaders.set("Content-Type", "application/json");
  if (!anonymous) {
    const token = getToken();
    if (token) finalHeaders.set("Authorization", `Bearer ${token}`);
  }

  let response: Response;
  try {
    response = await fetch(path, {
      ...rest,
      headers: finalHeaders,
      body: json !== undefined ? JSON.stringify(json) : (rest as RequestInit).body,
    });
  } catch (cause) {
    throw new ApiError(0, "Network error", "NETWORK_ERROR", cause);
  }

  if (response.status === 204) return undefined as T;

  const isJson = response.headers.get("content-type")?.includes("application/json");
  const body = isJson
    ? await response.json().catch(() => null)
    : await response.text().catch(() => null);

  if (response.ok) return body as T;

  const { message, code } = extractDetail(body);

  if (response.status === 401 && !skipAuthRedirect) {
    // One place decides what an expired session means, instead of 22 silent failures.
    clearToken();
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent(UNAUTHORIZED_EVENT));
    }
  }

  throw new ApiError(response.status, message ?? `Request failed (${response.status})`, code, body);
}

/** Convenience wrappers — `api.get("/api/users/me")` reads better at call sites. */
export const api = {
  get: <T = unknown>(path: string, options?: RequestOptions) =>
    apiFetch<T>(path, { ...options, method: "GET" }),
  post: <T = unknown>(path: string, json?: unknown, options?: RequestOptions) =>
    apiFetch<T>(path, { ...options, method: "POST", json }),
  patch: <T = unknown>(path: string, json?: unknown, options?: RequestOptions) =>
    apiFetch<T>(path, { ...options, method: "PATCH", json }),
  put: <T = unknown>(path: string, json?: unknown, options?: RequestOptions) =>
    apiFetch<T>(path, { ...options, method: "PUT", json }),
  delete: <T = unknown>(path: string, options?: RequestOptions) =>
    apiFetch<T>(path, { ...options, method: "DELETE" }),
};
